import { TokenIcon } from "@/components/brand/TokenIcon";
import { AnimatedNumber } from "@/components/defi/AnimatedNumber";
import type { ExtendedTokenId } from "@/lib/token-assets";
import { normalizeTokenId, TOKEN_ASSETS } from "@/lib/token-assets";
import { cn } from "@/lib/utils";

export type TokenAmountId = ExtendedTokenId;

function resolveToken(id: string): ExtendedTokenId {
	return (
		normalizeTokenId(id) ??
		(TOKEN_ASSETS[id as ExtendedTokenId]?.id as ExtendedTokenId) ??
		(id as ExtendedTokenId)
	);
}

/** Icon + ticker for chips, legends, table cells. */
export function TokenLabel({
	id,
	size = "sm",
	className,
	showTicker = true,
}: {
	id: TokenAmountId | string;
	size?: "sm" | "md" | "lg";
	className?: string;
	showTicker?: boolean;
}) {
	const token = resolveToken(String(id));
	const ticker = TOKEN_ASSETS[token]?.symbol ?? String(id);

	return (
		<span className={cn("inline-flex items-center gap-1.5", className)}>
			<TokenIcon token={token} size={size} plain />
			{showTicker && (
				<span className="font-medium text-foreground">{ticker}</span>
			)}
		</span>
	);
}

/** Icon + mono amount + optional ticker. */
export function TokenAmount({
	id,
	value,
	decimals = 2,
	size = "sm",
	showSymbol = true,
	className,
	animate = true,
}: {
	id: TokenAmountId | string;
	value?: number | null;
	decimals?: number;
	size?: "sm" | "md" | "lg";
	showSymbol?: boolean;
	className?: string;
	animate?: boolean;
}) {
	const token = resolveToken(String(id));
	const ticker = TOKEN_ASSETS[token]?.symbol ?? String(id);

	return (
		<span
			className={cn(
				"inline-flex min-w-0 items-center gap-1.5 font-mono-amount",
				className,
			)}
		>
			<TokenIcon token={token} size={size} plain />
			{value != null && Number.isFinite(value) && (
				<span className="font-semibold tabular-nums">
					{animate ? (
						<AnimatedNumber value={value} decimals={decimals} size="sm" />
					) : (
						value.toLocaleString("en-US", {
							minimumFractionDigits: Math.min(2, decimals),
							maximumFractionDigits: decimals,
						})
					)}
				</span>
			)}
			{showSymbol && (
				<span className="text-muted-foreground">{ticker}</span>
			)}
		</span>
	);
}
